import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
  Widgets as WidgetsIcon,
  PictureAsPdf as PdfIcon,
  FileDownload as CsvIcon
} from '@mui/icons-material';
import { getAuthHeaders } from '../utils/authUtils';
import { createApiUrl } from '../utils/apiConfig';

// Import statistics subcomponents
import PersonalStatistics from './statistics/PersonalStatistics';
import TeamStatistics from './statistics/TeamStatistics';
import ModalityStatistics from './statistics/ModalityStatistics';
import QueryBuilderStatistics from './statistics/QueryBuilderStatistics';

const STATISTICS_PAGE_CONTENT_ZOOM = 0.8;

const Statistics = () => {
  const { t } = useTranslation();
  
  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [availableTabs, setAvailableTabs] = useState([]);
  
  // Refs for statistics components to call their export functions
  const personalStatsRef = useRef(null);
  const teamStatsRef = useRef(null);
  const modalityStatsRef = useRef(null);
  const queryBuilderStatsRef = useRef(null);

  // Define all possible tabs with their permissions
  const allTabs = [
    {
      id: 'personal',
      label: t('statistics.tabs.personal', 'Persönliche'),
      icon: <PersonIcon />,
      permission: 'can_view_personal_stats',
      component: PersonalStatistics
    },
    {
      id: 'team',
      label: t('statistics.tabs.team', 'Team'),
      icon: <GroupIcon />,
      permission: 'can_view_team_stats',
      component: TeamStatistics
    },
    {
      id: 'modality',
      label: t('statistics.tabs.modality', 'Modalitäten'),
      icon: <CategoryIcon />,
      permission: 'can_view_modality_stats',
      component: ModalityStatistics
    },
    {
      id: 'queryBuilder',
      label: t('statistics.tabs.queryBuilder', 'Query-Builder'),
      icon: <WidgetsIcon />,
      permission: 'can_view_query_builder',
      component: QueryBuilderStatistics
    }
  ];

  // Load user data and permissions on component mount
  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(createApiUrl('auth/me'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        
        // Filter tabs based on user permissions
        const userTabs = allTabs.filter(tab => userData[tab.permission] === true);
        setAvailableTabs(userTabs);
        
        // Set first available tab as active
        if (userTabs.length > 0) {
          setActiveTab(0);
        }
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get the appropriate ref for the current tab
  const getCurrentRef = () => {
    if (availableTabs.length === 0) return null;
    const currentTab = availableTabs[activeTab];
    if (!currentTab) return null;
    
    switch (currentTab.id) {
      case 'personal':
        return personalStatsRef;
      case 'team':
        return teamStatsRef;
      case 'modality':
        return modalityStatsRef;
      case 'queryBuilder':
        return queryBuilderStatsRef;
      default:
        return null;
    }
  };

  // Handle download button clicks
  const handlePdfDownload = () => {
    const ref = getCurrentRef();
    if (ref?.current?.handlePdfDownload) {
      ref.current.handlePdfDownload();
    }
  };

  const handleCsvDownload = () => {
    const ref = getCurrentRef();
    if (ref?.current?.handleCsvDownload) {
      ref.current.handleCsvDownload();
    }
  };

  // Check if current tab has download permission
  const canDownload = () => {
    if (availableTabs.length === 0 || !currentUser) return false;
    const currentTab = availableTabs[activeTab];
    if (!currentTab) return false;
    
    switch (currentTab.id) {
      case 'personal':
        return currentUser.can_download_personal_stats === true;
      case 'team':
        return currentUser.can_download_team_stats === true;
      case 'modality':
        return currentUser.can_download_modality_stats === true;
      case 'queryBuilder':
        return currentUser.can_download_query_builder === true;
      default:
        return false;
    }
  };

  // Render content for the active tab
  const renderTabContent = () => {
    if (availableTabs.length === 0) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">
            {t('statistics.noPermissions', 'Sie haben keine Berechtigung, Statistiken anzuzeigen.')}
          </Alert>
        </Box>
      );
    }

    const currentTab = availableTabs[activeTab];
    if (!currentTab) return null;

    const Component = currentTab.component;
    
    // Pass the appropriate ref based on tab
    switch (currentTab.id) {
      case 'personal':
        return <Component ref={personalStatsRef} currentUser={currentUser} />;
      case 'team':
        return <Component ref={teamStatsRef} currentUser={currentUser} />;
      case 'modality':
        return <Component ref={modalityStatsRef} currentUser={currentUser} />;
      case 'queryBuilder':
        return <Component ref={queryBuilderStatsRef} currentUser={currentUser} />;
      default:
        return <Component currentUser={currentUser} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      px: { xs: 2, sm: 3 },
      boxSizing: 'border-box'
    }}>
      <Box sx={{
        zoom: STATISTICS_PAGE_CONTENT_ZOOM,
        width: '100%'
      }}>
      <Paper sx={{ 
        width: '100%', 
        mt: 0, 
        height: `calc((100vh - 150px) / ${STATISTICS_PAGE_CONTENT_ZOOM})`,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {availableTabs.length > 0 ? (
          <>
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 2, flex: 1 }}
              >
                {availableTabs.map((tab, index) => (
                  <Tab 
                    key={tab.id}
                    label={tab.label}
                    icon={tab.icon}
                    iconPosition="start"
                    sx={{ minHeight: 64 }}
                  />
                ))}
              </Tabs>
              
              {/* Download Buttons - rechts in der Tab-Leiste */}
              {canDownload() && (
                <Box sx={{ display: 'flex', gap: 1, pr: 2 }}>
                  <Tooltip title={t('statistics.personal.download.pdf', 'Als PDF herunterladen')}>
                    <IconButton onClick={handlePdfDownload} color="primary" size="medium">
                      <PdfIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('statistics.personal.download.csv', 'Als CSV herunterladen')}>
                    <IconButton onClick={handleCsvDownload} color="primary" size="medium">
                      <CsvIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {renderTabContent()}
            </Box>
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              {t('statistics.noPermissions', 'Sie haben keine Berechtigung, Statistiken anzuzeigen.')}
            </Alert>
          </Box>
        )}
      </Paper>
      </Box>
    </Box>
  );
};

export default Statistics;